import * as React from 'react'

import { getRandomColor } from '@campai/lib-common/utils/misc'

import { Slot } from '@/components/new/lib/slot'
import { cn } from '@/components/new/lib/utils'
import type { Color } from '@/components/new/ui/colors'

type HexColor = `#${string}`

export type BevelSurfaceColor = Color | HexColor

// The surface is tinted by a palette or hex color, or by the color a seed such as a name gets
type BevelSurfaceProps = React.ComponentPropsWithoutRef<'span'> & { asChild?: boolean } & (
        | { color: BevelSurfaceColor; seed?: never }
        | { color?: never; seed: string }
    )

// Grays mix in a lighter shade so their fill stays calm instead of turning into a heavy gray surface
const grayColors: BevelSurfaceColor[] = ['slate', 'gray', 'zinc', 'neutral', 'stone']

const hexColorRegex = /^#(?:[A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/

const isHexColor = (value: string): value is HexColor => {
    return hexColorRegex.test(value)
}

const getHueFromHexColor = (hexColor: HexColor) => {
    const normalizedHex =
        hexColor.length === 4 ? hexColor.replace(/./g, (char, index) => (index === 0 ? char : char + char)) : hexColor
    const r = Number.parseInt(normalizedHex.slice(1, 3), 16) / 255
    const g = Number.parseInt(normalizedHex.slice(3, 5), 16) / 255
    const b = Number.parseInt(normalizedHex.slice(5, 7), 16) / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min

    if (delta === 0) {
        return 0
    }

    if (max === r) {
        return ((g - b) / delta + (g < b ? 6 : 0)) * 60
    }
    if (max === g) {
        return ((b - r) / delta + 2) * 60
    }

    return ((r - g) / delta + 4) * 60
}

// Derives the glossy tinted look from an accent color per scheme: a fill that deepens towards the bottom, a border
// gradient behind it and a tight glow in the same hue. Dark mode mixes into the page background instead of staying
// translucent, so the border gradient does not show through the fill.
const getBevelSurfaceStyle = ({
    accentLight,
    accentDark,
    textLight,
    textDark,
}: {
    accentLight: string
    accentDark: string
    textLight: string
    textDark: string
}) =>
    ({
        '--bevel-bg-light': `color-mix(in oklab, ${accentLight} 19%, white)`,
        '--bevel-bg-end-light': `color-mix(in oklab, ${accentLight} 36%, white)`,
        '--bevel-border-light': `color-mix(in oklab, ${accentLight} 36%, white)`,
        '--bevel-border-end-light': `color-mix(in oklab, ${accentLight} 78%, white)`,
        '--bevel-glow-light': `color-mix(in oklab, color-mix(in oklab, ${accentLight} 40%, white) 75%, transparent)`,
        '--bevel-text-light': textLight,
        '--bevel-bg-dark': `color-mix(in oklab, ${accentDark} 16%, var(--background))`,
        '--bevel-bg-end-dark': `color-mix(in oklab, ${accentDark} 30%, var(--background))`,
        '--bevel-border-dark': `color-mix(in oklab, ${accentDark} 58%, var(--background))`,
        '--bevel-border-end-dark': `color-mix(in oklab, ${accentDark} 28%, var(--background))`,
        '--bevel-glow-dark': `color-mix(in oklab, ${accentDark} 30%, transparent)`,
        '--bevel-text-dark': textDark,
    }) as React.CSSProperties

// Paints only the soft bevel: the tinted fill, its gradient border, a light top edge and a glow, with text and icons in
// the matching shade. Shape, size, spacing and typography belong to the component that uses it.
const BevelSurface = React.forwardRef<HTMLSpanElement, BevelSurfaceProps>(
    ({ className, color, seed, style, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot.Root : 'span'
        const colorStyle = React.useMemo(() => {
            if (color === undefined) {
                const seededColor = getRandomColor(seed ?? '')

                return getBevelSurfaceStyle({
                    accentLight: seededColor.bgStrongLight,
                    accentDark: seededColor.bgStrongLight,
                    textLight: seededColor.textLight,
                    textDark: seededColor.textDark,
                })
            }

            if (isHexColor(color)) {
                const hue = getHueFromHexColor(color)

                return getBevelSurfaceStyle({
                    accentLight: `oklch(0.65 0.16 ${hue})`,
                    accentDark: `oklch(0.65 0.16 ${hue})`,
                    textLight: `oklch(0.38 0.1 ${hue})`,
                    textDark: `oklch(0.82 0.08 ${hue})`,
                })
            }

            const isGray = grayColors.includes(color)

            return getBevelSurfaceStyle({
                accentLight: `var(--color-${color}-${isGray ? 300 : 500})`,
                accentDark: `var(--color-${color}-${isGray ? 400 : 500})`,
                textLight: `var(--color-${color}-700)`,
                textDark: `var(--color-${color}-300)`,
            })
        }, [color, seed])
        const mergedStyle = React.useMemo(() => ({ ...colorStyle, ...style }), [colorStyle, style])

        return (
            <Comp
                ref={ref}
                data-slot="bevel-surface"
                className={cn(
                    'border border-transparent text-[var(--bevel-text-light)] [background:linear-gradient(var(--bevel-bg-light),var(--bevel-bg-end-light))_padding-box,linear-gradient(var(--bevel-border-light),var(--bevel-border-end-light))_border-box] shadow-[inset_0_1px_0_rgb(255_255_255/0.85),0_1px_2px_var(--bevel-glow-light)] dark:text-[var(--bevel-text-dark)] dark:[background:linear-gradient(var(--bevel-bg-dark),var(--bevel-bg-end-dark))_padding-box,linear-gradient(var(--bevel-border-dark),var(--bevel-border-end-dark))_border-box] dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_var(--bevel-glow-dark)]',
                    className
                )}
                style={mergedStyle}
                {...props}
            />
        )
    }
)

BevelSurface.displayName = 'BevelSurface'

export { BevelSurface }
